import { Node, Slice, Mark } from 'hubspot-prosemirror/pm/model';
import { Plugin } from 'hubspot-prosemirror/pm/state';
import {
  isUrlAndNotEmail,
  linkify,
  linkifyTextNode,
  LinkifyOptions,
} from './linkify';
import { EditorView } from 'hubspot-prosemirror/pm/view';
import { ensureUrlHasProtocol } from '../../../draft-transplant/utils/libUtils';

export type AutoDetectLinkOptions = LinkifyOptions;

function shouldTryToLinkify(nodeBefore: Node, event: KeyboardEvent) {
  const isAlreadyLink: boolean = nodeBefore.marks.some(
    (mark: Mark) => mark.type.name === 'link'
  );
  return (
    !isAlreadyLink &&
    (event.key === ' ' ||
      event.key.toLowerCase() === 'spacebar' ||
      event.key.toLowerCase() === 'enter' ||
      event.key === '\n')
  );
}

export function createAutoDetectLinkPlugin(
  options: AutoDetectLinkOptions = {}
) {
  const autoDetectBareUrls = Boolean(options.autoDetectBareUrls);

  return new Plugin({
    props: {
      transformPasted: (slice: Slice) => {
        const result = linkify(slice.content, options);
        return new Slice(result, slice.openStart, slice.openEnd);
      },
      handlePaste: (view: EditorView, event: ClipboardEvent, __slice) => {
        const pastedText = event.clipboardData?.getData('text/plain');
        if (
          pastedText &&
          isUrlAndNotEmail(pastedText, options) &&
          !view.state.selection.empty
        ) {
          const href = autoDetectBareUrls
            ? ensureUrlHasProtocol(pastedText)
            : pastedText;
          const tr = view.state.tr.addMark(
            view.state.selection.from,
            view.state.selection.to,
            view.state.schema.marks.link.create({ href })
          );
          view.dispatch(tr);
          return true;
        }
        return false;
      },
      handleKeyDown(view: EditorView, event: KeyboardEvent) {
        const { state, dispatch } = view;
        const { $from } = state.selection;
        const { nodeBefore } = $from;

        // Detect a ' ' or spacebar or Enter key press
        if (nodeBefore != null && shouldTryToLinkify(nodeBefore, event)) {
          const linkifiedTextNode: Node[] = linkifyTextNode(
            nodeBefore,
            options
          );
          const didLinkify =
            linkifiedTextNode.length > 0 &&
            linkifiedTextNode.some((node: Node) =>
              node.marks.some((mark: Mark) => mark.type.name === 'link')
            );

          if (didLinkify) {
            const textLength =
              nodeBefore.text == null ? 0 : nodeBefore.text.length;
            const replaceFrom = $from.pos - textLength;
            const replaceTo = $from.pos;
            const tr = state.tr.replaceWith(
              replaceFrom,
              replaceTo,
              linkifiedTextNode
            );
            dispatch(tr);
          }
        }

        return false; // Allow default handling for other keys
      },
    },
  });
}

export const AutoDetectLinkPlugin = createAutoDetectLinkPlugin();
